/**
 * The agent-first rail on a level (components/play/AgentRail.tsx,
 * lib/agent-first.ts): connect your own agent, tell it what you need (you
 * hold the task, owner 2026-10-30), watch what it spends, then
 * replay the level with a smaller model and see the difference.
 *
 * Glossary as in strings/agents.ts ("the agent" is الوكيل, der Agent,
 * エージェント). Claude Code, Codex, Cursor, MCP and `/model` stay Latin.
 * Nothing here promises a saving: the figure is the learner's own, measured.
 */
import type { Locale } from '../locale';

const en = {
  gateTitle: 'Bring your agent',
  gateIntro: 'You hold the task; your agent does the work. One command connects Claude Code to this workspace, then you tell it what you need, in your own words. We count what it spends, then show you how to get the same result for less.',
  getCommand: 'Get my command',
  pasteTitle: 'Paste this into a terminal',
  shellLabel: 'Your terminal',
  shellPosix: 'macOS, Linux, Git Bash',
  shellPowershell: 'Windows PowerShell',
  pasteHint: 'It starts Claude Code connected to this workspace, with cost reporting on for this session only: nothing is written to your settings. Keep this tab open: your agent works in it, and you check its work here.',
  keyKept: 'The command holds your agent key. It stays on this page until you close the tab.',
  lastingTitle: 'Set it up to last, or use another agent',
  again: 'Show the command again',
  signIn: 'Sign in to connect your agent',
  keyName: 'Claude Code',
  keyFailed: 'That did not work. Try again.',
  keyTooMany: 'You already have five keys. Revoke one on the agent page first.',
  keyPage: 'Manage keys',
  step2: 'Add EngineerOS to Claude Code',
  step3: 'Let Claude Code report what it spends',
  step3Hint: 'Put this in .claude/settings.json in the folder you run Claude Code from. We keep only each call\'s model, token counts and cost, never your prompts.',
  otherAgents: 'Codex, Cursor and other MCP agents can connect with the same key and the server address. Cost is measured for Claude Code only, for now.',
  waiting: 'Connected. Your agent is listening.',
  talkTitle: 'Then tell it what you need',
  talkHint: 'Your agent sees the files, not the brief: the task is yours to give. Brief it as you would a colleague: what you need, what done looks like, what must not change. Answer its questions. When it says it is done, press Check my work and tell it what the check found.',
  spent: 'Spent on this run',
  lastRun: 'Your last run cost',
  noCalls: 'No model calls counted yet.',
  calls: (n: number) => (n === 1 ? '1 model call' : `${n} model calls`),
  saving: 'Your saving on this level',
  firstPass: 'Your first pass cost',
  replayIntro: 'Now try one tip: quit Claude Code, press Replay, paste the command again, and brief your agent again with the tip in mind. A fresh conversation, the same task.',
  replay: 'Replay this level',
};

export const agentFirstCopy: Record<Locale, typeof en> = {
  en,
  ar: {
    gateTitle: en.gateTitle,
    gateIntro: en.gateIntro,
    getCommand: 'أعطني الأمر',
    pasteTitle: 'الصق هذا في الطرفية',
    shellLabel: 'طرفيتك',
    shellPosix: 'macOS وLinux وGit Bash',
    shellPowershell: 'Windows PowerShell',
    pasteHint: en.pasteHint,
    keyKept: 'الأمر يحمل مفتاح وكيلك، ويبقى في هذه الصفحة حتى تغلق التبويب.',
    lastingTitle: 'اجعل الإعداد دائمًا، أو استخدم وكيلًا آخر',
    again: 'أظهر الأمر مرة أخرى',
    signIn: 'سجّل الدخول لتربط وكيلك',
    keyName: 'Claude Code',
    keyFailed: 'لم ينجح ذلك. حاول مرة أخرى.',
    keyTooMany: 'لديك خمسة مفاتيح بالفعل. ألغِ أحدها في صفحة الوكيل أولًا.',
    keyPage: 'إدارة المفاتيح',
    step2: 'أضف EngineerOS إلى Claude Code',
    step3: 'اجعل Claude Code يبلّغ عمّا ينفقه',
    step3Hint: 'ضع هذا في ‎.claude/settings.json في المجلد الذي تشغّل منه Claude Code. نحتفظ فقط بالنموذج وعدد الرموز والتكلفة لكل طلب، ولا نحتفظ بأوامرك أبدًا.',
    otherAgents: en.otherAgents,
    waiting: en.waiting,
    // English only for now (owner, 2026-10-29).
    talkTitle: en.talkTitle,
    talkHint: en.talkHint,
    spent: 'ما أُنفق في هذه المحاولة',
    lastRun: 'تكلفة محاولتك الأخيرة',
    noCalls: 'لم تُحسب أي طلبات للنموذج بعد.',
    calls: (n: number) => (n === 1 ? 'طلب واحد للنموذج' : n === 2 ? 'طلبان للنموذج' : `${n} طلبات للنموذج`),
    saving: 'ما وفّرته في هذا المستوى',
    firstPass: 'تكلفة نجاحك الأول',
    replayIntro: en.replayIntro,
    replay: 'إعادة هذا المستوى',
  },
  de: {
    gateTitle: en.gateTitle,
    gateIntro: en.gateIntro,
    getCommand: 'Meinen Befehl holen',
    pasteTitle: 'Füge das in ein Terminal ein',
    shellLabel: 'Dein Terminal',
    shellPosix: 'macOS, Linux, Git Bash',
    shellPowershell: 'Windows PowerShell',
    pasteHint: en.pasteHint,
    keyKept: 'Der Befehl enthält deinen Agent-Schlüssel. Er bleibt auf dieser Seite, bis du den Tab schließt.',
    lastingTitle: 'Dauerhaft einrichten oder einen anderen Agenten nutzen',
    again: 'Befehl noch einmal zeigen',
    signIn: 'Anmelden, um deinen Agenten zu verbinden',
    keyName: 'Claude Code',
    keyFailed: 'Das hat nicht geklappt. Versuch es noch einmal.',
    keyTooMany: 'Du hast schon fünf Schlüssel. Widerrufe zuerst einen auf der Agenten-Seite.',
    keyPage: 'Schlüssel verwalten',
    step2: 'EngineerOS zu Claude Code hinzufügen',
    step3: 'Claude Code melden lassen, was es ausgibt',
    step3Hint: 'Leg das in .claude/settings.json in dem Ordner ab, aus dem du Claude Code startest. Wir speichern pro Aufruf nur Modell, Token-Zahlen und Kosten, nie deine Prompts.',
    otherAgents: en.otherAgents,
    waiting: en.waiting,
    // English only for now (owner, 2026-10-29).
    talkTitle: en.talkTitle,
    talkHint: en.talkHint,
    spent: 'Ausgegeben in diesem Lauf',
    lastRun: 'Dein letzter Lauf kostete',
    noCalls: 'Noch keine Modellaufrufe gezählt.',
    calls: (n: number) => (n === 1 ? '1 Modellaufruf' : `${n} Modellaufrufe`),
    saving: 'Deine Ersparnis in diesem Level',
    firstPass: 'Dein erster Erfolg kostete',
    replayIntro: en.replayIntro,
    replay: 'Level wiederholen',
  },
  ja: {
    gateTitle: en.gateTitle,
    gateIntro: en.gateIntro,
    getCommand: 'コマンドを取得',
    pasteTitle: 'これをターミナルに貼り付けてください',
    shellLabel: 'お使いのターミナル',
    shellPosix: 'macOS、Linux、Git Bash',
    shellPowershell: 'Windows PowerShell',
    pasteHint: en.pasteHint,
    keyKept: 'コマンドにはエージェントキーが含まれます。タブを閉じるまでこのページに表示されます。',
    lastingTitle: '設定を残す、または別のエージェントを使う',
    again: 'コマンドをもう一度表示',
    signIn: 'ログインしてエージェントを接続',
    keyName: 'Claude Code',
    keyFailed: 'うまくいきませんでした。もう一度お試しください。',
    keyTooMany: 'キーはすでに5つあります。先にエージェントのページで1つ取り消してください。',
    keyPage: 'キーを管理',
    step2: 'Claude Code に EngineerOS を追加',
    step3: 'Claude Code に使用額を報告させる',
    step3Hint: 'Claude Code を起動するフォルダーの .claude/settings.json にこれを入れてください。保存するのは各呼び出しのモデル、トークン数、コストだけで、プロンプトは保存しません。',
    otherAgents: en.otherAgents,
    waiting: en.waiting,
    // English only for now (owner, 2026-10-29).
    talkTitle: en.talkTitle,
    talkHint: en.talkHint,
    spent: '今回の使用額',
    lastRun: '前回の実行の使用額',
    noCalls: 'まだモデル呼び出しは計測されていません。',
    calls: (n: number) => `モデル呼び出し ${n} 回`,
    saving: 'このレベルでの節約',
    firstPass: '最初のクリアにかかった金額',
    replayIntro: en.replayIntro,
    replay: 'このレベルをやり直す',
  },
};
